import {
  AppShell,
  Burger,
  Group,
  Text,
  NavLink,
  Tooltip,
  Menu,
  ScrollArea,
  UnstyledButton,
  Avatar,
  Divider,
  Box,
  Button,
  Flex,
  Image,
  Badge,
  Stack,
} from "@mantine/core";
import {
  IconLayoutGrid,
  IconSearch,
  IconFileText,
  IconBriefcase,
  IconBookmark,
  IconHelpCircle,
  IconLogout,
  IconAlertTriangle,
  IconChevronRight,
  IconCoins,
  IconSettings,
  IconPencil,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { createContext, useContext, useState } from "react";
import { useUserContext } from "@/context/UserContext";
import { SidebarOnboardingTour } from "@/components/layout/SidebarOnboardingTour";
import AppUsageGuideBot from "@/components/profile/AppUsageGuideBot";

interface ProfileContextType {
  isProfileCreated: boolean;
  setIsProfileCreated: (value: boolean) => void;
}

const ProfileContext = createContext<ProfileContextType>({
  isProfileCreated: false,
  setIsProfileCreated: () => { },
});

export const useProfileStatus = () => useContext(ProfileContext);

// Company Status Banner Component
interface CompanyStatusBannerProps {
  adminBlocked: boolean;
  blockedReason?: string | null;
  companyStatus?: string;
  onViewMore: () => void;
}

function CompanyStatusBanner({ adminBlocked, blockedReason, companyStatus, onViewMore }: CompanyStatusBannerProps) {
  if (adminBlocked) {
    return (
      <Box
        pos="fixed"
        bottom={0}
        left={{ base: 0, sm: 300 }}
        right={0}
        py={16}
        px={32}
        style={{
          backgroundColor: "#FEE2E2",
          zIndex: 100,
          borderTop: "2px solid #FECACA",
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <IconAlertTriangle size={24} color="#991B1B" style={{ flexShrink: 0 }} />
            <Box style={{ minWidth: 0 }}>
              <Text size="md" fw={600} c="#991B1B">
                Your account has been temporarily blocked please contact support.
              </Text>
              <Text size="sm" c="#B91C1C" truncate>
                {blockedReason ||
                  "Your profile has been disabled. You will not be able to perform certain actions like shortlist, expressing interests, etc."}{" "}
              </Text>
            </Box>
          </Group>
          <Button
            variant="filled"
            color="#D84B4B"
            size="sm"
            rightSection={<IconChevronRight size={16} />}
            onClick={onViewMore}
            style={{ flexShrink: 0 }}
          >
            View more
          </Button>
        </Group>
      </Box>
    );
  }

  if (companyStatus === "ACTION_REQUIRED") {
    return (
      <Box
        pos="fixed"
        bottom={0}
        left={{ base: 0, sm: 300 }}
        right={0}
        py={16}
        px={32}
        style={{
          backgroundColor: "#FEE2E2",
          zIndex: 100,
          borderTop: "2px solid #FECACA",
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <IconAlertTriangle size={24} color="#991B1B" style={{ flexShrink: 0 }} />
            <Box style={{ minWidth: 0 }}>
              <Text size="md" fw={600} c="#991B1B">
                Your company could not be verified
              </Text>
              <Text size="sm" c="#B91C1C" truncate>
                Click to know more
              </Text>
            </Box>
          </Group>
          <Button
            variant="filled"
            color="#D84B4B"
            size="sm"
            rightSection={<IconChevronRight size={16} />}
            onClick={onViewMore}
            style={{ flexShrink: 0 }}
          >
            Go to Profile
          </Button>
        </Group>
      </Box>
    );
  }

  return null;
}

export function AppLayout() {
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure();
  const [desktopOpened] = useDisclosure(true);
  const [isProfileCreated, setIsProfileCreated] = useState(false);
  const { userDetails, logout, shortlistedIds, pendingRequestCounts } = useUserContext();
  const shortlistCount = shortlistedIds.size;
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const navItems = [
    { label: "My Profile", icon: IconLayoutGrid, path: "/profile" },
    { label: "Explore Companies", icon: IconSearch, path: "/my-discovery" },
    { label: "Shortlist", icon: IconBookmark, path: "/shortlist" },
    { label: "Requests", icon: IconFileText, path: "/requests" },
    { label: "Collaborations", icon: IconBriefcase, path: "/collaborations" },
    // { label: "Invoices", icon: IconReceipt, path: "/invoices" },
  ];

  const handleNavClick = (path: string) => {
    if (location.pathname === "/create-profile") return;
    navigate(path);
    closeMobile();
  };

  const isDashboard = location.pathname === "/create-profile";

  const handleLogout = () => {
    logout();
  };

  // Company status banner handling
  const isAdminBlocked = userDetails?.company?.adminBlocked || false;
  const blockedReason = userDetails?.company?.blockedReason;
  const companyStatus = userDetails?.company?.status;
  const showStatusBanner = isAdminBlocked || (companyStatus === "ACTION_REQUIRED" && location.pathname !== "/profile");

  const handleStatusBannerClick = () => {
    if (isAdminBlocked) {
      navigate("/settings"); // Navigate to settings/support for blocked accounts
    } else if (companyStatus === "ACTION_REQUIRED") {
      navigate("/profile"); // Navigate to edit profile for action required
    }
  };

  return (
    <ProfileContext.Provider value={{ isProfileCreated, setIsProfileCreated }}>
      <AppShell
        header={{ height: { base: 60, sm: 0 } }}
        navbar={{
          width: 300,
          breakpoint: "sm",
          collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
        }}
        padding="0"
      >
        <AppShell.Header hiddenFrom="sm">
          <Group h="100%" px="md" justify="space-between">
            <Image src="/gtLogo.svg" alt="GTsetu" style={{ height: 40, width: "auto" }} />
            <Burger opened={mobileOpened} onClick={toggleMobile} size="sm" color="var(--mantine-color-primary-7)" />
          </Group>
        </AppShell.Header>

        <AppShell.Navbar
          p="md"
          style={{
            backgroundColor: "var(--mantine-color-primary-6)",
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 40px)",
            backgroundSize: "auto",
            backgroundPosition: "top left",
            borderRight: "1px solid rgba(255, 255, 255, 0.14)",
          }}
        >
          {/* HEADER / BRANDING */}
          <AppShell.Section mb="md" visibleFrom="sm">
            <Group justify="center" align="center">
              <Flex align="center">
                <Image
                  src="/gtLogo.svg"
                  alt="GTsetu"
                  h={60}
                  w="auto"
                  style={{ filter: "brightness(0) invert(1)" }}
                />
              </Flex>

              <Burger
                opened={mobileOpened}
                onClick={toggleMobile}
                hiddenFrom="sm"
                size="sm"
                color="var(--mantine-color-primary-7)"
              />
            </Group>

            <Divider mt="sm" color="rgba(255, 255, 255, 0.9)" />
          </AppShell.Section>

          {/* NAV LINKS */}
          <AppShell.Section grow component={ScrollArea}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {navItems.map((item) => {
                const disabled = isDashboard;
                const active = isActive(item.path);
                const sidebarTourId = item.path.replace(/^\//, "");

                return (
                  <Box key={item.label} data-sidebar-tour={sidebarTourId}>
                    <Tooltip
                      label="Complete your profile to unlock this feature"
                      disabled={!disabled}
                      position="right"
                      withArrow
                    >
                      <NavLink
                        label={
                          item.path === "/requests" ? (
                            <Stack gap={6} align="stretch" style={{ width: "100%" }}>
                              <Text
                                size="sm"
                                fw={600}
                                className="sidebar-nav-text"
                                c={active ? "primary.7" : "white"}
                              >
                                {item.label}
                              </Text>
                              <Group gap="sm" wrap="wrap">
                                <Text
                                  size="xs"
                                  fw={600}
                                  className="sidebar-nav-sub"
                                  c={active ? "primary.7" : "rgba(255, 255, 255, 0.85)"}
                                >
                                  Sent: {pendingRequestCounts.sent}
                                </Text>
                                <Text
                                  size="xs"
                                  fw={600}
                                  className="sidebar-nav-sub"
                                  c={active ? "primary.7" : "rgba(255, 255, 255, 0.85)"}
                                >
                                  Received: {pendingRequestCounts.received}
                                </Text>
                              </Group>
                            </Stack>
                          ) : (
                            <Group justify="space-between" style={{ width: "100%" }}>
                              <Text
                                size="sm"
                                fw={600}
                                className="sidebar-nav-text"
                                c={active ? "primary.7" : "white"}
                              >
                                {item.label}
                              </Text>

                              {item.path === "/shortlist" && shortlistCount > 0 && (
                                <Badge size="xs" radius="xl" color="#DC2626">
                                  {shortlistCount}
                                </Badge>
                              )}
                            </Group>
                          )
                        }
                        leftSection={
                          <item.icon
                            size="1.2rem"
                            stroke={1.8}
                            color={active ? "var(--mantine-color-primary-7)" : "white"}
                          />
                        }
                        active={active}
                        onClick={() => handleNavClick(item.path)}
                        disabled={disabled}
                        variant="subtle"
                        styles={{
                          root: {
                            borderRadius: 8,
                            padding: "10px 12px",
                            opacity: disabled ? 0.5 : 1,
                            cursor: disabled ? "not-allowed" : "pointer",
                            backgroundColor: active ? "#ffc300" : "transparent",
                            color: active ? "var(--mantine-color-primary-7)" : "white",
                            "&:hover": {
                              backgroundColor: "#ffc300",
                              color: "var(--mantine-color-primary-7)",
                            },
                            "&:hover svg": {
                              color: "var(--mantine-color-primary-7)",
                            },
                            "&:hover .sidebar-nav-text": {
                              color: "var(--mantine-color-primary-7)",
                            },
                            "&:hover .sidebar-nav-sub": {
                              color: "var(--mantine-color-primary-7)",
                            },
                          },
                          label: {
                            color: "inherit",
                          },
                        }}
                      />
                    </Tooltip>
                  </Box>
                );
              })}
            </div>
          </AppShell.Section>

          {/* USER PROFILE */}
          <AppShell.Section>
            <Menu shadow="md" width="target" position="top">
              <Menu.Target>
                <UnstyledButton
                  w="100%"
                  p="xs"
                  style={{
                    borderRadius: 8,
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.12)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <Group>
                    <Avatar
                      radius="xl"
                      style={{
                        backgroundColor: "white",
                        border: "1px solid rgba(255, 255, 255, 0.18)",
                      }}
                    >
                      <IconSettings size={18} color="var(--mantine-color-primary-7)" />
                    </Avatar>
                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <Text size="sm" fw={600} c="white" truncate>
                        {userDetails?.company?.legalName || "Company Name"}
                      </Text>
                      <Text c="white" size="xs" truncate style={{ opacity: 0.85 }}>
                        {userDetails?.company?.gtid || "User"}
                      </Text>
                    </div>
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown
                style={{
                  backgroundColor: "var(--mantine-color-primary-6)",
                  border: "1px solid rgba(255, 255, 255, 0.14)",
                }}
              >
                <Menu.Item
                  c="white"
                  fw={600}
                  leftSection={<IconPencil size={16} />}
                  onClick={() => navigate("/profile/edit")}
                  style={{ borderRadius: 8 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#ffc300";
                    e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "white";
                  }}
                >
                  Edit Profile
                </Menu.Item>
                <Menu.Item
                  c="white"
                  fw={600}
                  leftSection={<IconCoins size={16} />}
                  onClick={() => navigate("/credits")}
                  style={{ borderRadius: 8 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#ffc300";
                    e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "white";
                  }}
                >
                  Credits
                </Menu.Item>
                <Menu.Item
                  c="white"
                  fw={600}
                  leftSection={<IconHelpCircle size={16} />}
                  onClick={() => navigate("/settings")}
                  style={{ borderRadius: 8 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#ffc300";
                    e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "white";
                  }}
                >
                  Settings
                </Menu.Item>
                <Menu.Item
                  c="white"
                  fw={600}
                  leftSection={<IconLogout size={16} />}
                  onClick={handleLogout}
                  style={{ borderRadius: 8 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#ffc300";
                    e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "white";
                  }}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </AppShell.Section>
        </AppShell.Navbar>

        <AppShell.Main>
          <Box style={{ paddingBottom: showStatusBanner ? 100 : 0 }}>
            <Outlet />
          </Box>
          <CompanyStatusBanner
            adminBlocked={isAdminBlocked}
            blockedReason={blockedReason}
            companyStatus={location.pathname.startsWith("/profile") ? undefined : companyStatus}
            onViewMore={handleStatusBannerClick}
          />
          <SidebarOnboardingTour />
          <AppUsageGuideBot />
        </AppShell.Main>
      </AppShell>
    </ProfileContext.Provider>
  );
}
